import { FilePlus, Eye, Share2, ShieldCheck, Lock, Globe, FileLock, Check, X, Clock, Mail, AlertCircle, FolderOpen, BellRing, Users, FileText, MessageSquare } from "lucide-react";

export const homePricing = [
  {
    id: "starter",
    name: "Starter",
    description: "Perfect for smaller firms getting started.",
    price: "£49",
    period: "per month",
    badge: null,
    featured: false,
    bullets: [
      "Up to 25 active cases",
      "Timeline generator",
      "Client share link",
      "Basic branding",
      "Email updates",
    ],
    cta: "Start Free Trial",
  },
  {
    id: "pro",
    name: "Pro",
    description: "The plan most firms choose.",
    price: "£99",
    period: "per month",
    badge: "Most Popular",
    featured: true,
    bullets: [
      "Unlimited active cases",
      "Client portal login",
      "Document uploads",
      "Custom branding",
      "Case templates",
      "Priority support",
    ],
    cta: "Start Free Trial",
  },
  {
    id: "premium",
    name: "Premium",
    description: "For larger firms that need full control.",
    price: "£149",
    period: "per month",
    badge: null,
    featured: false,
    bullets: [
      "Everything in Pro",
      "Multi-user team management",
      "Team roles & permissions",
      "Full white-label",
      "Advanced reporting",
      "Priority onboarding & support",
    ],
    cta: "Start Free Trial",
  },
];

export const painPoints = [
  { icon: AlertCircle, text: "Clients don't understand the leasehold process or timeline." },
  { icon: Mail, text: "Repetitive 'what's the status?' emails clog every inbox." },
  { icon: Clock, text: "Unclear milestones and slipping deadlines erode trust." },
  { icon: FolderOpen, text: "Documents and updates scattered across email, drives and shared folders." },
];

export const howItWorks = [
  {
    n: "01",
    icon: FilePlus,
    title: "Create a case",
    body: "Open a new lease extension or enfranchisement case in seconds — pre-built UK templates do the heavy lifting (Section 42, collective enfranchisement, statutory and informal routes).",
  },
  {
    n: "02",
    icon: Eye,
    title: "Generate a visual timeline",
    body: "LeaseFlow auto-plots statutory deadlines, valuation windows, counter-notice periods and completion targets onto a single timeline your team and client can both read.",
  },
  {
    n: "03",
    icon: Share2,
    title: "Share secure client access",
    body: "Invite your client to a read-only portal. They see exactly where the case is, what's next, and which documents they need to action. No more weekly update emails.",
  },
];

export const features = [
  { icon: Clock, title: "Dynamic leasehold timeline generator", desc: "Statutory dates calculated automatically from the date of the initial notice. Counter-notices, tribunal windows, valuation deadlines — all plotted." },
  { icon: Users, title: "Secure client portal", desc: "Read-only client view of their case. They see status, next steps, and required actions without you sending another update email." },
  { icon: FileText, title: "Case status tracking", desc: "Each case has a single live status — Initial Notice Served, Awaiting Counter-Notice, Valuation, Negotiation, Completion — visible to your whole team." },
  { icon: FolderOpen, title: "Document sharing & vault", desc: "Upload, organise and share case documents from one place. Title plans, leases, valuations, draft transfers — all version-controlled." },
  { icon: BellRing, title: "Automated email updates", desc: "Clients receive scheduled progress emails — generated from real case data, branded to your firm. Set once, runs forever." },
  { icon: FileLock, title: "UK lease extension templates", desc: "Pre-built templates for Section 42 notice, Section 13 collective enfranchisement, Right to Manage applications, and informal extensions." },
  { icon: ShieldCheck, title: "GDPR compliant infrastructure", desc: "UK-hosted (AWS eu-west-2), AES-256 at rest, ICO registered. Built to the data-handling standards UK solicitors are required to meet." },
  { icon: MessageSquare, title: "Client messaging audit trail", desc: "Every message and document share is logged. Useful for SRA evidence, complaint defence and case handovers." },
];

export const comparison = {
  left: { name: "Email & spreadsheets", className: "text-rose-600" },
  right: { name: "LeaseFlow", className: "text-emerald-600" },
  rows: [
    { left: "Client emails 'any update?' weekly", right: "Client checks portal — sees status instantly", leftIcon: X, rightIcon: Check },
    { left: "Deadlines tracked in head + diary", right: "Statutory deadlines auto-calculated", leftIcon: X, rightIcon: Check },
    { left: "Documents scattered across systems", right: "Single secure case vault per file", leftIcon: X, rightIcon: Check },
    { left: "No audit trail for complaints", right: "Full activity log for SRA evidence", leftIcon: X, rightIcon: Check },
    { left: "Onboarding new staff takes weeks", right: "Templates onboard new fee-earners in days", leftIcon: X, rightIcon: Check },
    { left: "Looks unprofessional to clients", right: "Modern client experience worth talking about", leftIcon: X, rightIcon: Check },
  ],
};

export const useCases = [
  { name: "Lease extensions", desc: "Section 42 statutory route, informal extensions, tribunal references — full lifecycle support." },
  { name: "Collective enfranchisement", desc: "Section 13 notices, qualifying tenant tracking, nominee purchaser setup, completion." },
  { name: "Right to manage (RTM)", desc: "Section 79 notices, qualifying flats, counter-notices, FTT applications." },
];

export const trustItems = [
  { icon: ShieldCheck, label: "GDPR compliant" },
  { icon: Lock, label: "AES-256 encryption at rest" },
  { icon: Globe, label: "UK-hosted (AWS eu-west-2)" },
  { icon: FileLock, label: "SRA-friendly audit trail" },
];
